import type { Sku } from "../types";

/**
 * OpenAI, called directly. A prompt over 272K tokens prices the whole call at the long-context rows;
 * priority and fast cost twice the default tier, flex half. A cache write costs 1.25 times uncached
 * input for thirty minutes and 2.5 times for twelve hours. OpenAI reports every cache read as one
 * count, priced on the implicit read row.
 */
export const openaiSkus: readonly Sku[] = [
  {
    id: "openai:gpt-6-sol:global:default",
    billedBy: "openai",
    model: "gpt-6-sol",
    endpoint: "global",
    tier: "default",
    promptTokensCount: "allInput",
    maxOutputTokens: 128_000,
    prices: [
      {
        id: "uncachedInput",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 2,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "implicitCacheRead",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.2,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite30m",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 2.5,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite12h",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 5,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "output",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 10,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "uncachedInput.over-272k",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 4,
        promptTokens: { over: 272_000 },
      },
      {
        id: "implicitCacheRead.over-272k",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.4,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite30m.over-272k",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 5,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite12h.over-272k",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 10,
        promptTokens: { over: 272_000 },
      },
      {
        id: "output.over-272k",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 15,
        promptTokens: { over: 272_000 },
      },
      {
        id: "tool:web_search",
        meter: "tool:web_search",
        unit: "1k uses",
        currency: "USD",
        amount: 10,
      },
    ],
  },
  {
    id: "openai:gpt-6-sol:global:priority",
    billedBy: "openai",
    model: "gpt-6-sol",
    endpoint: "global",
    tier: "priority",
    promptTokensCount: "allInput",
    maxOutputTokens: 128_000,
    prices: [
      {
        id: "uncachedInput",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 4,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "implicitCacheRead",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.4,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite30m",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 5,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite12h",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 10,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "output",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 20,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "uncachedInput.over-272k",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 8,
        promptTokens: { over: 272_000 },
      },
      {
        id: "implicitCacheRead.over-272k",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.8,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite30m.over-272k",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 10,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite12h.over-272k",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 20,
        promptTokens: { over: 272_000 },
      },
      {
        id: "output.over-272k",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 30,
        promptTokens: { over: 272_000 },
      },
      {
        id: "tool:web_search",
        meter: "tool:web_search",
        unit: "1k uses",
        currency: "USD",
        amount: 10,
      },
    ],
  },
  {
    id: "openai:gpt-6-sol:global:fast",
    billedBy: "openai",
    model: "gpt-6-sol",
    endpoint: "global",
    tier: "fast",
    promptTokensCount: "allInput",
    maxOutputTokens: 128_000,
    prices: [
      {
        id: "uncachedInput",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 4,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "implicitCacheRead",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.4,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite30m",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 5,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite12h",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 10,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "output",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 20,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "uncachedInput.over-272k",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 8,
        promptTokens: { over: 272_000 },
      },
      {
        id: "implicitCacheRead.over-272k",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.8,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite30m.over-272k",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 10,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite12h.over-272k",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 20,
        promptTokens: { over: 272_000 },
      },
      {
        id: "output.over-272k",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 30,
        promptTokens: { over: 272_000 },
      },
      {
        id: "tool:web_search",
        meter: "tool:web_search",
        unit: "1k uses",
        currency: "USD",
        amount: 10,
      },
    ],
  },
  {
    id: "openai:gpt-6-sol:global:flex",
    billedBy: "openai",
    model: "gpt-6-sol",
    endpoint: "global",
    tier: "flex",
    promptTokensCount: "allInput",
    maxOutputTokens: 128_000,
    prices: [
      {
        id: "uncachedInput",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 1,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "implicitCacheRead",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.1,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite30m",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 1.25,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite12h",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 2.5,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "output",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 5,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "uncachedInput.over-272k",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 2,
        promptTokens: { over: 272_000 },
      },
      {
        id: "implicitCacheRead.over-272k",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.2,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite30m.over-272k",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 2.5,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite12h.over-272k",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 5,
        promptTokens: { over: 272_000 },
      },
      {
        id: "output.over-272k",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 7.5,
        promptTokens: { over: 272_000 },
      },
      {
        id: "tool:web_search",
        meter: "tool:web_search",
        unit: "1k uses",
        currency: "USD",
        amount: 10,
      },
    ],
  },
  {
    id: "openai:gpt-6.1-sol:global:default",
    billedBy: "openai",
    model: "gpt-6.1-sol",
    endpoint: "global",
    tier: "default",
    promptTokensCount: "allInput",
    maxOutputTokens: 128_000,
    prices: [
      {
        id: "uncachedInput",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 2,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "implicitCacheRead",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.1,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite30m",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 2.5,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite12h",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 5,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "output",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 10,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "uncachedInput.over-272k",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 4,
        promptTokens: { over: 272_000 },
      },
      {
        id: "implicitCacheRead.over-272k",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.2,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite30m.over-272k",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 5,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite12h.over-272k",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 10,
        promptTokens: { over: 272_000 },
      },
      {
        id: "output.over-272k",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 15,
        promptTokens: { over: 272_000 },
      },
      {
        id: "tool:web_search",
        meter: "tool:web_search",
        unit: "1k uses",
        currency: "USD",
        amount: 10,
      },
    ],
  },
  {
    id: "openai:gpt-6.1-sol:global:priority",
    billedBy: "openai",
    model: "gpt-6.1-sol",
    endpoint: "global",
    tier: "priority",
    promptTokensCount: "allInput",
    maxOutputTokens: 128_000,
    prices: [
      {
        id: "uncachedInput",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 4,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "implicitCacheRead",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.2,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite30m",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 5,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite12h",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 10,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "output",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 20,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "uncachedInput.over-272k",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 8,
        promptTokens: { over: 272_000 },
      },
      {
        id: "implicitCacheRead.over-272k",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.4,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite30m.over-272k",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 10,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite12h.over-272k",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 20,
        promptTokens: { over: 272_000 },
      },
      {
        id: "output.over-272k",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 30,
        promptTokens: { over: 272_000 },
      },
      {
        id: "tool:web_search",
        meter: "tool:web_search",
        unit: "1k uses",
        currency: "USD",
        amount: 10,
      },
    ],
  },
  {
    id: "openai:gpt-6.1-sol:global:fast",
    billedBy: "openai",
    model: "gpt-6.1-sol",
    endpoint: "global",
    tier: "fast",
    promptTokensCount: "allInput",
    maxOutputTokens: 128_000,
    prices: [
      {
        id: "uncachedInput",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 4,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "implicitCacheRead",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.2,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite30m",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 5,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite12h",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 10,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "output",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 20,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "uncachedInput.over-272k",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 8,
        promptTokens: { over: 272_000 },
      },
      {
        id: "implicitCacheRead.over-272k",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.4,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite30m.over-272k",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 10,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite12h.over-272k",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 20,
        promptTokens: { over: 272_000 },
      },
      {
        id: "output.over-272k",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 30,
        promptTokens: { over: 272_000 },
      },
      {
        id: "tool:web_search",
        meter: "tool:web_search",
        unit: "1k uses",
        currency: "USD",
        amount: 10,
      },
    ],
  },
  {
    id: "openai:gpt-6.1-sol:global:flex",
    billedBy: "openai",
    model: "gpt-6.1-sol",
    endpoint: "global",
    tier: "flex",
    promptTokensCount: "allInput",
    maxOutputTokens: 128_000,
    prices: [
      {
        id: "uncachedInput",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 1,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "implicitCacheRead",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.05,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite30m",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 1.25,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite12h",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 2.5,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "output",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 5,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "uncachedInput.over-272k",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 2,
        promptTokens: { over: 272_000 },
      },
      {
        id: "implicitCacheRead.over-272k",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.1,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite30m.over-272k",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 2.5,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite12h.over-272k",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 5,
        promptTokens: { over: 272_000 },
      },
      {
        id: "output.over-272k",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 7.5,
        promptTokens: { over: 272_000 },
      },
      {
        id: "tool:web_search",
        meter: "tool:web_search",
        unit: "1k uses",
        currency: "USD",
        amount: 10,
      },
    ],
  },
  {
    id: "openai:gpt-6-luna:global:default",
    billedBy: "openai",
    model: "gpt-6-luna",
    endpoint: "global",
    tier: "default",
    promptTokensCount: "allInput",
    maxOutputTokens: 128_000,
    prices: [
      {
        id: "uncachedInput",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.1,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "implicitCacheRead",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.01,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite30m",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.125,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite12h",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.25,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "output",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.5,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "uncachedInput.over-272k",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.2,
        promptTokens: { over: 272_000 },
      },
      {
        id: "implicitCacheRead.over-272k",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.02,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite30m.over-272k",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.25,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite12h.over-272k",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.5,
        promptTokens: { over: 272_000 },
      },
      {
        id: "output.over-272k",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.75,
        promptTokens: { over: 272_000 },
      },
      {
        id: "tool:web_search",
        meter: "tool:web_search",
        unit: "1k uses",
        currency: "USD",
        amount: 10,
      },
    ],
  },
  {
    id: "openai:gpt-6-luna:global:priority",
    billedBy: "openai",
    model: "gpt-6-luna",
    endpoint: "global",
    tier: "priority",
    promptTokensCount: "allInput",
    maxOutputTokens: 128_000,
    prices: [
      {
        id: "uncachedInput",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.2,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "implicitCacheRead",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.02,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite30m",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.25,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite12h",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.5,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "output",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 1,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "uncachedInput.over-272k",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.4,
        promptTokens: { over: 272_000 },
      },
      {
        id: "implicitCacheRead.over-272k",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.04,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite30m.over-272k",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.5,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite12h.over-272k",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 1,
        promptTokens: { over: 272_000 },
      },
      {
        id: "output.over-272k",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 1.5,
        promptTokens: { over: 272_000 },
      },
      {
        id: "tool:web_search",
        meter: "tool:web_search",
        unit: "1k uses",
        currency: "USD",
        amount: 10,
      },
    ],
  },
  {
    id: "openai:gpt-6-luna:global:fast",
    billedBy: "openai",
    model: "gpt-6-luna",
    endpoint: "global",
    tier: "fast",
    promptTokensCount: "allInput",
    maxOutputTokens: 128_000,
    prices: [
      {
        id: "uncachedInput",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.2,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "implicitCacheRead",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.02,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite30m",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.25,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite12h",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.5,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "output",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 1,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "uncachedInput.over-272k",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.4,
        promptTokens: { over: 272_000 },
      },
      {
        id: "implicitCacheRead.over-272k",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.04,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite30m.over-272k",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.5,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite12h.over-272k",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 1,
        promptTokens: { over: 272_000 },
      },
      {
        id: "output.over-272k",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 1.5,
        promptTokens: { over: 272_000 },
      },
      {
        id: "tool:web_search",
        meter: "tool:web_search",
        unit: "1k uses",
        currency: "USD",
        amount: 10,
      },
    ],
  },
  {
    id: "openai:gpt-6-luna:global:flex",
    billedBy: "openai",
    model: "gpt-6-luna",
    endpoint: "global",
    tier: "flex",
    promptTokensCount: "allInput",
    maxOutputTokens: 128_000,
    prices: [
      {
        id: "uncachedInput",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.05,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "implicitCacheRead",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.005,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite30m",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.0625,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "cacheWrite12h",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.125,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "output",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.25,
        promptTokens: { upTo: 272_000 },
      },
      {
        id: "uncachedInput.over-272k",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.1,
        promptTokens: { over: 272_000 },
      },
      {
        id: "implicitCacheRead.over-272k",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.01,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite30m.over-272k",
        meter: "cacheWrite30m",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.125,
        promptTokens: { over: 272_000 },
      },
      {
        id: "cacheWrite12h.over-272k",
        meter: "cacheWrite12h",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.25,
        promptTokens: { over: 272_000 },
      },
      {
        id: "output.over-272k",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.375,
        promptTokens: { over: 272_000 },
      },
      {
        id: "tool:web_search",
        meter: "tool:web_search",
        unit: "1k uses",
        currency: "USD",
        amount: 10,
      },
    ],
  },
];
